import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import AdminLogin from './components/AdminLogin';
import MotDePasseOublie from './features/auth/MotDePasseOublie';
import ReinitialiserMotDePasse from './features/auth/ReinitialiserMotDePasse';
import EcranMotDePasseObligatoire from './features/auth/EcranMotDePasseObligatoire';

// V2 - Interface principale SAV KLY
import AdminV2 from './AdminV2';

// ============================================
// PROTECTED ROUTE WRAPPER
// ============================================
// Protège les routes nécessitant une authentification

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brume">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-graphite">Chargement...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// ============================================
// PUBLIC ROUTE WRAPPER
// ============================================
// Redirige vers le dashboard si déjà authentifié

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4" />
          <p className="text-graphite/70">Chargement...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Après connexion, retour à la page demandée (ex. lien partagé vers /dossiers/:id).
    return <Navigate to={destinationApresConnexion(location.state)} replace />;
  }

  return <>{children}</>;
};

/** Page à rouvrir après la connexion : celle mémorisée par ProtectedRoute, sinon l'accueil. */
export function destinationApresConnexion(etat: unknown): string {
  const from = (etat as { from?: { pathname?: string; search?: string } } | null)?.from;
  const chemin = from?.pathname;
  if (!chemin || !chemin.startsWith('/') || chemin.startsWith('//') || chemin === '/login') return '/';
  return chemin + (from?.search ?? '');
}

// ============================================
// APP ROUTES
// ============================================

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AdminLogin />
          </PublicRoute>
        }
      />

      {/* Mot de passe : accessibles sans être connecté (lien reçu par e-mail). */}
      <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
      <Route path="/reinitialiser/:jeton" element={<ReinitialiserMotDePasse />} />

      {/* SAV KLY - Interface principale. Elle déclare ses propres routes,
          dont la fiche d'un dossier : /dossiers/:id (voir AdminV2). */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminV2 />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// ============================================
// APP COMPONENT
// ============================================

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        {/* Par-dessus tout l'écran quand le serveur exige un nouveau mot de passe. */}
        <EcranMotDePasseObligatoire />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
